import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";

// Public page for a parent/guardian: review notice → enter emailed code → declaration → consent recorded.
export default function GuardianConsentPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [code, setCode] = useState("");
  const [declare, setDeclare] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/privacy/guardian/request?token=${encodeURIComponent(token)}`).then((r) => setInfo(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Invalid link"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { await api.post("/privacy/guardian/verify", { token, code, declaration: declare }); setDone(true); }
    catch (e2) { setError(formatApiErrorDetail(e2.response?.data?.detail) || "Could not verify"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] px-4 py-10 text-white">
      <div className="mx-auto max-w-xl">
        <Logo />
        <div data-testid="guardian-consent-page" className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
          <h1 className="flex items-center gap-2 text-xl font-bold"><ShieldCheck className="h-5 w-5 text-cyan-400" /> Parent / guardian consent</h1>
          {done ? (
            <div data-testid="guardian-success" className="mt-6 rounded-xl border border-emerald-400/30 bg-emerald-400/5 p-4 text-sm text-emerald-200">
              <CheckCircle2 className="mb-2 h-6 w-6" /> Thank you. Your consent has been recorded and {info?.student_name}'s AI assessment and coach are now enabled. You can withdraw this consent at any time by emailing our Grievance Officer.
            </div>
          ) : error && !info ? (
            <p data-testid="guardian-error" className="mt-4 text-sm text-rose-400">{error}</p>
          ) : info ? (
            <form onSubmit={submit} className="mt-4 space-y-4 text-sm text-slate-300">
              <p><b className="text-white">{info.student_name}</b> (under 18) has created an EntranceZone AI account and named you as their parent or guardian. Under the Digital Personal Data Protection Act, 2023 we need your verifiable consent before processing a child's personal data for the purposes below.</p>
              <div className="rounded-xl border border-white/10 p-3 text-xs">
                <p className="font-semibold text-cyan-300">What will be processed and why</p>
                <ul className="mt-2 space-y-1">{info.notice.items.filter((i) => i.required).map((i) => <li key={i.data}>• <b className="text-white">{i.data}</b> — {i.why}</li>)}</ul>
                <p className="mt-2"><b className="text-white">AI:</b> {info.notice.ai_processing}</p>
                <p className="mt-1"><b className="text-white">Never for children:</b> targeted advertising, behavioural tracking, marketing messages.</p>
                <p className="mt-1">{info.notice.grievance} · <Link to="/privacy" target="_blank" className="text-cyan-400 underline">Full Privacy Policy</Link></p>
              </div>
              {info.status !== "pending" || info.expired ? (
                <p data-testid="guardian-expired" className="text-amber-300">This request is {info.expired ? "expired" : info.status}. Ask the student to resend it from their account.</p>
              ) : (
                <>
                  <label className="block">Confirmation code from the email
                    <input data-testid="guardian-code-input" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" required
                      className="mt-1 w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-lg tracking-[0.4em] text-white outline-none focus:border-cyan-400/50" /></label>
                  <label className="flex items-start gap-2 text-xs">
                    <input data-testid="guardian-declaration-checkbox" type="checkbox" checked={declare} onChange={(e) => setDeclare(e.target.checked)} className="mt-0.5 accent-cyan-400" />
                    <span>I declare that I am the parent or lawful guardian of this child, I am an adult, and I give consent to the processing described above.</span>
                  </label>
                  {error && <p data-testid="guardian-error" className="text-rose-400">{error}</p>}
                  <button data-testid="guardian-submit-btn" type="submit" disabled={busy || code.length !== 6 || !declare}
                    className="flex items-center gap-2 rounded-xl bg-cyan-400 px-5 py-3 font-bold text-[#041016] disabled:opacity-50">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Give consent"}</button>
                </>
              )}
            </form>
          ) : <p className="mt-4 text-slate-400">Loading…</p>}
        </div>
      </div>
    </div>
  );
}
